import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, orderBy, getDocs, where, Timestamp, limit } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { collectionPath } from '../../utils/firestoreCollections';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, ChartTooltip, Legend, Filler
);

const LOG_COLLECTIONS = [
  { key: 'logsCustomer', label: 'Customer' },
  { key: 'logsOpp', label: 'Opportunity' },
  { key: 'logsTask', label: 'Task' },
  { key: 'logsContact', label: 'Contact' },
  { key: 'logsUser', label: 'User' },
  { key: 'logsOrder', label: 'Order' },
  { key: 'securityLogs', label: 'Security' },
  { key: 'logsCalendar', label: 'Calendar' },
];

const RANGE_OPTIONS = [
  { value: '7', label: 'Last 7 Days' },
  { value: '14', label: 'Last 14 Days' },
  { value: '30', label: 'Last 30 Days' },
  { value: '90', label: 'Last 90 Days' },
  { value: 'custom', label: 'Custom' },
];

const COLORS = [
  '#6366f1', '#f59e0b', '#10b981', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899'
];

const LogAnalyticsTab = ({ user }) => {
  const [range, setRange] = useState('30');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [rawLogs, setRawLogs] = useState([]);
  const [usersMap, setUsersMap] = useState(new Map()); // uid/email -> displayName

  // Load users map for display name enrichment
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const snap = await getDocs(collection(db, collectionPath('users')));
        const map = new Map();
        snap.docs.forEach(d => {
          const data = d.data();
          const name = data.displayName || '';
          if (name) {
            if (data.uid) map.set(data.uid, name);
            if (data.email) {
              map.set(data.email, name);
              map.set(data.email.split('@')[0], name);
            }
          }
        });
        setUsersMap(map);
      } catch (e) {
        console.warn('Failed to load users for analytics:', e);
      }
    };
    loadUsers();
  }, []);

  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      try {
        let cutoff, endDate;
        if (range === 'custom') {
          if (!dateFrom) return setLoading(false);
          cutoff = new Date(dateFrom);
          cutoff.setHours(0, 0, 0, 0);
          if (dateTo) {
            endDate = new Date(dateTo);
            endDate.setHours(23, 59, 59, 999);
          }
        } else {
          const days = parseInt(range);
          cutoff = new Date();
          cutoff.setDate(cutoff.getDate() - days);
          cutoff.setHours(0, 0, 0, 0);
        }

        const allLogs = [];
        await Promise.all(LOG_COLLECTIONS.map(async ({ key, label }) => {
          const ref = collection(db, collectionPath(key));
          const constraints = [
            orderBy('timestamp', 'desc'),
            where('timestamp', '>=', Timestamp.fromDate(cutoff)),
          ];
          if (endDate) {
            constraints.push(where('timestamp', '<=', Timestamp.fromDate(endDate)));
          }
          constraints.push(limit(2000));
          const q = query(ref, ...constraints);
          const snapshot = await getDocs(q);
          snapshot.docs.forEach(doc => {
            const data = doc.data();
            const ts = data.timestamp?.toDate ? data.timestamp.toDate() : (data.timestamp ? new Date(data.timestamp) : null);
            const uid = data.userId || data.actorId || '';
            const email = data.actorEmail || '';
            const rawName = data.userName || data.actorName || '';
            const resolvedName = usersMap.get(uid)
              || usersMap.get(email)
              || (email && usersMap.get(email.split('@')[0]))
              || rawName
              || email
              || 'Unknown';
            allLogs.push({
              collection: label,
              action: data.action || 'Unknown',
              level: data.level || 'info',
              userName: resolvedName,
              userId: uid,
              timestamp: ts,
            });
          });
        }));

        setRawLogs(allLogs);
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [range, dateFrom, dateTo, usersMap]);

  // Activity over time
  const activityOverTime = useMemo(() => {
    const labels = [];
    const counts = [];

    let startDate, endDateChart;
    if (range === 'custom' && dateFrom) {
      startDate = new Date(dateFrom);
      endDateChart = dateTo ? new Date(dateTo) : new Date();
    } else {
      const days = parseInt(range);
      startDate = new Date();
      startDate.setDate(startDate.getDate() - (days - 1));
      endDateChart = new Date();
    }
    startDate.setHours(0, 0, 0, 0);
    endDateChart.setHours(23, 59, 59, 999);

    const totalDays = Math.max(1, Math.ceil((endDateChart - startDate) / (1000 * 60 * 60 * 24)));
    for (let i = 0; i < totalDays; i++) {
      const d = new Date(startDate);
      d.setDate(d.getDate() + i);
      const key = d.toISOString().split('T')[0];
      labels.push(key);
      counts.push(0);
    }

    rawLogs.forEach(log => {
      if (!log.timestamp) return;
      const key = log.timestamp.toISOString().split('T')[0];
      const idx = labels.indexOf(key);
      if (idx !== -1) counts[idx]++;
    });

    return {
      labels: labels.map(l => {
        const d = new Date(l + 'T00:00:00');
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }),
      datasets: [{
        label: 'Log Entries',
        data: counts,
        borderColor: '#6366f1',
        backgroundColor: 'rgba(99, 102, 241, 0.1)',
        fill: true,
        tension: 0.4,
      }]
    };
  }, [rawLogs, range, dateFrom, dateTo]);

  // Logs by collection (doughnut)
  const byCollection = useMemo(() => {
    const map = {};
    LOG_COLLECTIONS.forEach(c => { map[c.label] = 0; });
    rawLogs.forEach(log => { map[log.collection] = (map[log.collection] || 0) + 1; });

    const filteredLabels = Object.keys(map).filter(k => map[k] > 0);
    return {
      labels: filteredLabels,
      datasets: [{
        data: filteredLabels.map(k => map[k]),
        backgroundColor: COLORS.slice(0, filteredLabels.length),
        borderWidth: 2,
        borderColor: '#fff',
      }]
    };
  }, [rawLogs]);

  // Top users (group by userId to avoid duplicates)
  const topUsers = useMemo(() => {
    const countMap = {}; // userId -> count
    const nameMap = {};  // userId -> displayName
    rawLogs.forEach(log => {
      const key = log.userId || log.userName || 'Unknown';
      countMap[key] = (countMap[key] || 0) + 1;
      if (!nameMap[key] || nameMap[key] === 'Unknown') {
        nameMap[key] = log.userName || 'Unknown';
      }
    });
    const sorted = Object.entries(countMap).sort((a, b) => b[1] - a[1]).slice(0, 10);
    return {
      labels: sorted.map(([key]) => {
        const name = nameMap[key] || key;
        return name.length > 20 ? name.substring(0, 20) + '...' : name;
      }),
      datasets: [{
        label: 'Actions',
        data: sorted.map(([, count]) => count),
        backgroundColor: '#6366f1',
        borderRadius: 4,
      }]
    };
  }, [rawLogs]);

  // Action type distribution
  const byAction = useMemo(() => {
    const map = {};
    rawLogs.forEach(log => {
      const action = log.action || 'Unknown';
      map[action] = (map[action] || 0) + 1;
    });
    const sorted = Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 10);
    return {
      labels: sorted.map(([action]) => action),
      datasets: [{
        label: 'Count',
        data: sorted.map(([, count]) => count),
        backgroundColor: COLORS.slice(0, sorted.length).map(c => c + '33'),
        borderColor: COLORS.slice(0, sorted.length),
        borderWidth: 1,
        borderRadius: 4,
      }]
    };
  }, [rawLogs]);

  // Level breakdown
  const levelCounts = useMemo(() => {
    const map = { info: 0, warn: 0, error: 0, debug: 0 };
    rawLogs.forEach(log => {
      const level = (log.level || 'info').toLowerCase();
      map[level] = (map[level] || 0) + 1;
    });
    return map;
  }, [rawLogs]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { display: false },
      tooltip: { mode: 'index', intersect: false }
    },
    scales: {
      y: { beginAtZero: true, ticks: { precision: 0 } }
    }
  };

  return (
    <div>
      {/* Controls */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-sm font-medium text-gray-600">Time Range:</label>
          {RANGE_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setRange(opt.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                range === opt.value
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {opt.label}
            </button>
          ))}
          {loading && <span className="text-sm text-indigo-600 animate-pulse ml-2">Loading...</span>}
        </div>
        {range === 'custom' && (
          <div className="flex flex-wrap items-end gap-3 mt-3 pt-3 border-t border-gray-100">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Date From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Date To</label>
              <input
                type="date"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            {!dateFrom && (
              <p className="text-xs text-amber-600 self-center">Select a start date to load analytics.</p>
            )}
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-xs font-medium text-gray-500 uppercase">Total Logs</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{rawLogs.length.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-blue-200 p-4">
          <p className="text-xs font-medium text-blue-600 uppercase">Info</p>
          <p className="text-2xl font-bold text-blue-700 mt-1">{levelCounts.info.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-yellow-200 p-4">
          <p className="text-xs font-medium text-yellow-600 uppercase">Warnings</p>
          <p className="text-2xl font-bold text-yellow-700 mt-1">{levelCounts.warn.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-red-200 p-4">
          <p className="text-xs font-medium text-red-600 uppercase">Errors</p>
          <p className="text-2xl font-bold text-red-700 mt-1">{levelCounts.error.toLocaleString()}</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Activity Over Time */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h4 className="font-semibold text-gray-800 mb-3">Activity Over Time</h4>
          <Line data={activityOverTime} options={{
            ...chartOptions,
            plugins: { ...chartOptions.plugins, legend: { display: false } }
          }} />
        </div>

        {/* By Collection */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h4 className="font-semibold text-gray-800 mb-3">Logs by Type</h4>
          <div className="max-w-[280px] mx-auto">
            <Doughnut data={byCollection} options={{
              responsive: true,
              maintainAspectRatio: true,
              plugins: {
                legend: { position: 'bottom', labels: { padding: 12, usePointStyle: true } }
              }
            }} />
          </div>
        </div>

        {/* Top Users */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h4 className="font-semibold text-gray-800 mb-3">Top Users by Activity</h4>
          <Bar data={topUsers} options={{
            ...chartOptions,
            indexAxis: 'y',
            scales: {
              x: { beginAtZero: true, ticks: { precision: 0 } },
              y: { ticks: { font: { size: 11 } } }
            }
          }} />
        </div>

        {/* Action Distribution */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h4 className="font-semibold text-gray-800 mb-3">Action Distribution</h4>
          <Bar data={byAction} options={chartOptions} />
        </div>
      </div>
    </div>
  );
};

export default LogAnalyticsTab;
